import { z } from 'zod';
/**
 * Ngân sách dự án — APM-AI-n (`plan-ai-features.md` §10.1).
 *
 * ## Đơn giá theo người là dữ liệu lương
 *
 * `CostRateSubject` cho ba cách khai, và thứ tự ấy có chủ đích: nhiều tổ chức
 * **không** muốn nhập lương từng người vào một công cụ quản lý dự án. Ép họ làm
 * vậy là cách chắc chắn để tính năng ngân sách không được dùng — nên khai theo
 * chức vụ hoặc theo kỹ năng phải đủ để ra một con số có nghĩa.
 *
 * Đọc đơn giá cấp **người** ghi `sensitive_access_log`, cùng luật với dữ liệu
 * hiệu suất (luật #5).
 */
export const CostRateSubject = z.enum(['user', 'project_role', 'skill']);
export const CostItemKind = z.enum(['equipment', 'license', 'infra', 'vendor', 'travel', 'other']);
/**
 * Tần suất một khoản chi — `daily`/`weekly`/`yearly` thêm 2026-09-30. Khoản định
 * kỳ nhân theo số kỳ còn lại tới ngày kết thúc dự án (`occurrencesLeft` ở
 * `@apm/estimate`; khoản năm làm tròn lên).
 */
export const CostRecurrence = z.enum(['once', 'daily', 'weekly', 'monthly', 'yearly']);
export const CostRateView = z.object({
    id: z.uuid(),
    subjectType: CostRateSubject,
    subjectId: z.uuid(),
    /** Tên người, chức vụ hoặc kỹ năng — giao diện không phải tra thêm một vòng. */
    subjectName: z.string().nullable(),
    hourlyRate: z.number().nonnegative(),
    currency: z.string().length(3),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
});
export const CreateCostRateInput = z.object({
    subjectType: CostRateSubject,
    subjectId: z.uuid(),
    hourlyRate: z.number().positive().max(100_000_000),
    currency: z.string().length(3).default('VND'),
    effectiveFrom: z.string(),
    /**
     * Bỏ trống = còn hiệu lực. Đổi giá thì **đóng dòng cũ** rồi thêm dòng mới —
     * không sửa tại chỗ, nếu không mọi báo cáo chi phí lịch sử sai ngay lần đầu.
     */
    effectiveTo: z.string().nullable().optional(),
});
export const CostItemView = z.object({
    id: z.uuid(),
    kind: CostItemKind,
    name: z.string(),
    amount: z.number().nonnegative(),
    currency: z.string().length(3),
    recurrence: CostRecurrence,
    fromDate: z.string().nullable(),
    toDate: z.string().nullable(),
    taskId: z.uuid().nullable(),
});
export const CreateCostItemInput = z.object({
    kind: CostItemKind,
    name: z.string().trim().min(1).max(200),
    amount: z.number().positive().max(1_000_000_000_000),
    currency: z.string().length(3).default('VND'),
    recurrence: CostRecurrence.default('once'),
    fromDate: z.string().nullable().optional(),
    toDate: z.string().nullable().optional(),
    taskId: z.uuid().nullable().optional(),
});
export const BudgetView = z.object({
    id: z.uuid(),
    approvedAmount: z.number().nonnegative(),
    currency: z.string().length(3),
    contingencyPct: z.number().min(0).max(100),
    version: z.number().int().positive(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const ApproveBudgetInput = z.object({
    approvedAmount: z.number().positive().max(1_000_000_000_000),
    currency: z.string().length(3).default('VND'),
    contingencyPct: z.number().min(0).max(100).default(0),
    note: z.string().max(500).optional(),
});
/** Mọi thứ một màn hình ngân sách cần, trong một lời gọi. */
export const ProjectBudgetOverview = z.object({
    projectId: z.uuid(),
    /** Mức duyệt mới nhất; `null` khi chưa ai duyệt. */
    budget: BudgetView.nullable(),
    costItems: z.array(CostItemView),
    /**
     * Đơn giá đã **gộp** theo chức vụ và kỹ năng.
     *
     * Đơn giá theo **người** chỉ có mặt khi người gọi có `budget:manage`, và lần
     * đọc ấy được ghi `sensitive_access_log`.
     */
    rates: z.array(CostRateView),
    ratesIncludePeople: z.boolean(),
});
/**
 * Ước lượng chi phí của một dự án — APM-AI-o.
 *
 * `missing` là phần quan trọng ngang con số: một tổng không nói nó thiếu gì thì
 * đọc như một tổng chắc chắn.
 */
export const CostEstimateView = z.object({
    projectId: z.uuid(),
    /** Mốc tính; `null` = hiện tại. */
    asOf: z.string().nullable(),
    labour: z.number().nonnegative(),
    items: z.number().nonnegative(),
    contingency: z.number().nonnegative(),
    total: z.number().nonnegative(),
    currency: z.string().length(3),
    confidence: z.enum(['low', 'medium', 'high']),
    /** Số task tính được đầy đủ trên tổng số. */
    covered: z.number().int().nonnegative(),
    taskCount: z.number().int().nonnegative(),
    /**
     * Trong `covered`, bao nhiêu task có **hệ số tốc độ** khác `1` —
     * docs/plan-project-rate-speed.md.
     *
     * Một tổng phải nói được nó dựng từ đâu. Không có con số này thì "dự án tốn
     * 800 triệu" và "dự án tốn 800 triệu **vì ta giả định đội này nhanh hơn mức
     * chuẩn 30%**" đọc y hệt nhau — và giả định thứ hai là thứ người duyệt cần
     * thấy để cãi lại.
     */
    speedAdjusted: z.number().int().nonnegative(),
    missing: z.array(z.object({ kind: z.enum(['task_hours', 'rate']), taskId: z.uuid() })).max(200),
    /**
     * Vài việc tốn nhiều tiền nhất — APM-AI-o.
     *
     * Một tổng nói **bao nhiêu**; danh sách này nói **ở đâu**. Không có nó thì
     * câu hỏi đầu tiên của mọi người đọc ("tiền đi đâu?") chỉ trả lời được bằng
     * cách mở từng task ra tính tay — và đó chính là file Excel mà tính năng này
     * ra đời để thay thế.
     *
     * Chỉ có phần **tính được**: việc thiếu giờ hoặc thiếu đơn giá nằm ở `missing`
     * chứ không nằm đây với con số 0. Một hàng 0 đồng đọc như "việc này rẻ", trong
     * khi sự thật là "chưa biết".
     */
    topTasks: z
        .array(z.object({
        taskId: z.uuid(),
        /** `null` ở nhánh `asOf`: ảnh chụp quá khứ không giữ mã và tiêu đề. */
        taskKey: z.string().max(40).nullable(),
        title: z.string().max(500).nullable(),
        hours: z.number().nonnegative(),
        cost: z.number().nonnegative(),
    }))
        .max(10),
    /** Mức đã duyệt để so; `null` khi chưa ai duyệt. */
    approvedAmount: z.number().nonnegative().nullable(),
});
/**
 * Chi phí ước lượng **theo thời gian** — APM-AI-o, luật #7.
 *
 * ## Vì sao số điểm bị chặn cứng
 *
 * Mỗi điểm là một lượt replay đầy đủ để dựng lại task tại mốc ấy. Tám điểm là
 * tám lượt; một biểu đồ "mượt" ba mươi điểm là ba mươi lượt cho **một** lần mở
 * tab, và nó sẽ trông vô hại trên dữ liệu thử.
 *
 * ## Vì sao endpoint riêng, không phải một vòng lặp `fetch`
 *
 * Cùng lý do: một vòng lặp ở trình duyệt biến con số ấy thành tám request, mỗi
 * cái mở một transaction. Server quét một lần và trả cả loạt.
 */
export const COST_SERIES_MAX_POINTS = 8;
export const CostSeriesPoint = z.object({
    /** Mốc của điểm, ISO 8601. */
    at: z.iso.datetime(),
    labour: z.number().nonnegative(),
    items: z.number().nonnegative(),
    contingency: z.number().nonnegative(),
    total: z.number().nonnegative(),
    /** Bao nhiêu việc tính được trên tổng, tại mốc ấy — đọc kèm `total`. */
    covered: z.number().int().nonnegative(),
    taskCount: z.number().int().nonnegative(),
});
export const CostSeriesView = z.object({
    projectId: z.uuid(),
    currency: z.string().length(3),
    points: z.array(CostSeriesPoint).max(COST_SERIES_MAX_POINTS),
    /** Mức đã duyệt để vẽ một đường ngang so sánh; `null` khi chưa ai duyệt. */
    approvedAmount: z.number().nonnegative().nullable(),
});
/**
 * Kết quả ước lượng ngân sách bằng AI — APM-AI-p (`plan-ai-features.md` §10.4).
 *
 * ## `assumptions` không phải là dữ liệu
 *
 * Người dùng trả lời `ask_user` ("đơn giá backend khoảng 250k") thì câu ấy thành
 * một **giả định** trong output, không thành một hàng `cost_rate`. Ghi thẳng là
 * AI ghi dữ liệu chính thức (luật #6), và một đơn giá gõ vội trong một câu chat
 * sẽ im lặng chi phối mọi báo cáo chi phí về sau. Giao diện có nút "Lưu giả định
 * này thành dữ liệu thật" chạy một command riêng, do người bấm.
 */
export const BudgetAssumption = z.object({
    text: z.string().min(1).max(300),
    /** Giả định này ảnh hưởng bao nhiêu tiền trong tổng. */
    affectsAmount: z.number().nonnegative(),
    /**
     * Dạng **có cấu trúc** của giả định, khi nó là một đơn giá — APM-AI-p.
     *
     * Có trường này thì giao diện hiện được nút "Lưu thành dữ liệu thật"; không
     * có thì giả định vẫn là một câu chữ và người dùng tự đi khai. Nút ấy chạy
     * `POST /admin/cost-rates` **do người bấm**, cần `budget:manage`, và ngoài
     * lượt chạy AI — luật #6 nguyên vẹn.
     *
     * `subjectId` phải là một id từ `subjectsWithoutRate` của `get_budget_inputs`:
     * grounding check đòi mọi Id trong output có mặt trong kết quả tool, nên một
     * id bịa làm hỏng cả lượt chạy thay vì lặng lẽ tạo ra một hàng đơn giá sai
     * chủ thể.
     *
     * ⚠️ Không có `user` trong `subjectType`. Đơn giá theo người là dữ liệu
     * lương, và cả đường đi này chạy trên bản đã gộp — đề nghị một mức lương cho
     * một cá nhân là đúng thứ tính năng ngân sách được dựng để không làm.
     */
    proposedRate: z
        .object({
        subjectType: z.enum(['project_role', 'skill']),
        subjectId: z.uuid(),
        hourlyRate: z.number().positive().max(100_000_000),
        currency: z.string().length(3).default('VND'),
    })
        .nullable()
        .optional(),
});
export const BudgetEstimateOutput = z.object({
    total: z.object({
        amount: z.number().nonnegative(),
        currency: z.string().length(3),
    }),
    /** Phân rã theo nhóm việc hoặc pha — chỗ tiền thật sự nằm. */
    breakdown: z
        .array(z.object({
        group: z.string().min(1).max(120),
        amount: z.number().nonnegative(),
        confidence: z.enum(['low', 'medium', 'high']),
    }))
        .max(20),
    assumptions: z.array(BudgetAssumption).max(10),
    /** Thứ còn thiếu để con số chắc hơn — viết cho người đọc, không phải mã lỗi. */
    missingInputs: z.array(z.string().min(1).max(200)).max(20),
    /** So với mức đã duyệt; `null` khi chưa ai duyệt. */
    vsApproved: z.object({ delta: z.number(), pct: z.number() }).nullable(),
    /** Nhận xét: hạng mục lớn nhất, chỗ tự tin thấp, task đắt bất thường. */
    narrative: z.string().min(1).max(2_000),
    /**
     * Độ tin cậy của **cả con số**, do hệ thống chốt — §0.4 luật 3 (APM-H-u).
     *
     * Model không điền trường này: `finalize` đọc `confidence` từ kết quả của
     * `estimate_project_cost` — tức từ **tỉ lệ task tính được**, do `projectCost`
     * tính. Để model tự phong là mời nó nói "cao" về một tổng dựng từ nửa số
     * việc, và một mức tin cậy sai trông y hệt một mức đúng nên người duyệt không
     * có cách nào cãi lại.
     *
     * `null` khi lượt chạy không gọi công cụ ước lượng nào — ví dụ đường đối
     * chứng gọi thẳng model. "Chưa chốt được" phải khác "thấp".
     */
    confidence: z.enum(['low', 'medium', 'high']).nullable().optional(),
    /** Căn cứ của mức trên ("dựng từ 12/40 việc"), để nó kiểm được chứ không chỉ tin được. */
    confidenceNote: z.string().max(200).nullable().optional(),
});
//# sourceMappingURL=budget.js.map